import { useMemo } from "react";
import { useWorkerLayout } from "@/hooks/useWorkerLayout";
import { useOrdersContext } from "@/context/OrdersContext";

type UseWorkspaceStatusArgs = {
  /**
   * Used to check hire status from `/orders` (OrdersContext).
   */
  serviceId?: string | null | undefined;
  workspaceId?: string | null | undefined;
  isHired?: boolean | null | undefined;
};

export function useWorkspaceStatus(args: UseWorkspaceStatusArgs = {}) {
  const { workspaces } = useWorkerLayout();
  const { orders } = useOrdersContext();

  const hasWorkspace = useMemo(
    () => (workspaces?.length ?? 0) >= 1,
    [workspaces],
  );

  const isHiredService = useMemo(() => {
    const serviceId = args.serviceId?.trim();
    const workspaceId = args.workspaceId?.trim();

    // Prefer authoritative source: orders list (already fetched from `/orders`).
    if (serviceId) {
      const hasMatchingOrder = orders?.some((o) => {
        if (!o || typeof o !== "object") return false;
        const order = o as {
          serviceId?: string;
          workspaceId?: string;
          workspace?: { id?: string };
        };
        if (order.serviceId !== serviceId) return false;
        if (!workspaceId) return false;
        return (order.workspaceId || order.workspace?.id) === workspaceId;
      });
      if (typeof hasMatchingOrder === "boolean") return hasMatchingOrder;
    }

    // Fallback for callers that already know hire state (e.g. service page).
    return Boolean(args.isHired);
  }, [args.isHired, args.serviceId, args.workspaceId, orders]);

  return { workspaces, hasWorkspace, isHiredService };
}

